import { Injectable } from '@angular/core';
import { IAgencyOfficersDetailsState } from './agency-officer-store.state';
import { Store } from '@ngrx/store';
import * as AgencyOfficerDetailsActions from './agency-officer-store.actions';
import * as AgencyOfficerDetailsSelectors from './agency-officer-store.selectors';
import { Observable, distinctUntilChanged, filter, of, switchMap } from 'rxjs';
import { AgencyOfficerDetailsPermissions } from '../models/officer-details.models';
import { AgencyOfficerDetailsRequest, OfficerPersmissionsRequest } from '../models/officer-details-request.models';
import { compareValuesSelectedOfficerUid } from '../functions/comparison.functions';
import { ActionType, UpdateOfficerDetailsPermissionsActionResult } from '../models/officer-details-action-result.models';

@Injectable({
  providedIn: 'root',
})
export class AgencyOfficerDetailsFacade {
  public constructor(private readonly store: Store<IAgencyOfficersDetailsState>) {}

  public fetchAgencyOfficerDetails(uid: string): void {
    this.store.dispatch(AgencyOfficerDetailsActions.fetchAgencyOfficerDetails({ uid }));
  }

  public getAgencyOfficerDetails(): Observable<AgencyOfficerDetailsPermissions> {
    return this.store.select(AgencyOfficerDetailsSelectors.agencyOfficerDetailsState).pipe(filter((x): x is AgencyOfficerDetailsPermissions => !!x));
  }

  public getAgencyOfficerDetailsEmitDistinctUidValue(): Observable<AgencyOfficerDetailsPermissions> {
    return this.store.select(AgencyOfficerDetailsSelectors.agencyOfficerDetailsState).pipe(
      filter((x): x is AgencyOfficerDetailsPermissions => !!x),
      distinctUntilChanged((previous, current) => compareValuesSelectedOfficerUid(previous, current))
    );
  }

  public updateAgencyOfficerDetails(agencyOfficerDetails: AgencyOfficerDetailsRequest): void {
    this.store.dispatch(AgencyOfficerDetailsActions.updateAgencyOfficerDetails({ agencyOfficerDetails: agencyOfficerDetails }));
  }

  public updateAgencyOfficerPermissions(agencyOfficerPermissions: OfficerPersmissionsRequest): void {
    this.store.dispatch(AgencyOfficerDetailsActions.updateAgencyOfficerPermissions({ agencyOfficerPermissions: agencyOfficerPermissions }));
  }

  public isLoading(): Observable<boolean> {
    return this.store.select(AgencyOfficerDetailsSelectors.isLoading);
  }

  public isImageLoading(): Observable<boolean> {
    return this.store.select(AgencyOfficerDetailsSelectors.isImageLoading);
  }

  public updateAgencyOfficerPhoto(photo: File): void {
    this.store.dispatch(AgencyOfficerDetailsActions.updateAgencyOfficerPhoto({ photo: photo }));
  }

  public updateAgencyOfficerActionResult(actionType: ActionType): Observable<UpdateOfficerDetailsPermissionsActionResult> {
    return this.isLoading().pipe(
      switchMap((isLoading) => {
        if (isLoading) {
          return of();
        }

        return this.store
          .select(AgencyOfficerDetailsSelectors.actionResult)
          .pipe(filter((x): x is UpdateOfficerDetailsPermissionsActionResult => !!x && x.actionType === actionType));
      })
    );
  }
  public clearState(): void {
    this.store.dispatch(AgencyOfficerDetailsActions.clearState());
  }
}
